import type {ComponentProps} from '@enonic/react-components'
import type {BreadcrumbItem} from '../primitives'
import type {PersonData} from './PersonProcessor'
import * as React from 'react'
import {Container, ResponsiveImage} from '../primitives'

const sanitizePhone = (phone: string): string => phone.replace(/[^+0-9]/g, '')

export const Person = (props: ComponentProps) => {
    const data = props.data as PersonData
    const commonData = props.common as Record<string, unknown> | undefined
    const breadcrumbs = commonData?.breadcrumbs as BreadcrumbItem[] | undefined
    const {title, jobTitle, department, bio, email, phone, image} = data

    return (
        <section data-hero className="mb-12 md:mb-20">
            <Container className="flex flex-col gap-6 px-6 pt-10 pb-16 md:gap-8 md:px-24 md:pt-16 md:pb-24">
                {breadcrumbs && breadcrumbs.length > 1 && (
                    <nav aria-label="Brødsmuler" className="col-span-full">
                        <a
                            href={breadcrumbs[breadcrumbs.length - 2].url}
                            className="inline-flex items-center gap-1 text-base leading-snug tracking-wide text-slate-700 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300 md:hidden"
                        >
                            <span aria-hidden="true">←</span>
                            {breadcrumbs[breadcrumbs.length - 2].title}
                        </a>
                        <ol className="hidden items-center gap-2 text-lg leading-snug tracking-wide md:flex">
                            {breadcrumbs.map((item, index) => {
                                const isLast = index === breadcrumbs.length - 1
                                return (
                                    <li key={index} className="flex items-center gap-2">
                                        {index > 0 && (
                                            <span className="text-slate-700" aria-hidden="true">/</span>
                                        )}
                                        {isLast ? (
                                            <span className="text-black" aria-current="page">{item.title}</span>
                                        ) : (
                                            <a
                                                href={item.url}
                                                className="text-slate-700 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300"
                                            >
                                                {item.title}
                                            </a>
                                        )}
                                    </li>
                                )
                            })}
                        </ol>
                    </nav>
                )}

                <div className="col-span-full grid grid-cols-12 gap-6 md:gap-8">
                    <div className="col-span-12 flex flex-col gap-5 md:col-span-7">
                        <h1 className="font-serif text-4xl leading-tightest text-blue-900 md:text-7xl">
                            {title}
                        </h1>
                        {(jobTitle || department) && (
                            <p className="text-xl leading-snug text-slate-700 md:text-2xl">
                                {jobTitle}
                                {jobTitle && department && <span aria-hidden="true"> · </span>}
                                {department}
                            </p>
                        )}
                        {bio && (
                            <p className="max-w-prose text-lg leading-snug text-slate-900 md:text-xl">
                                {bio}
                            </p>
                        )}
                        {(email || phone) && (
                            <ul className="flex flex-col gap-2 text-lg leading-snug">
                                {email && (
                                    <li>
                                        <a
                                            href={`mailto:${email}`}
                                            className="text-blue-900 underline hover:no-underline focus-visible:outline-1 focus-visible:outline-slate-300"
                                        >
                                            {email}
                                        </a>
                                    </li>
                                )}
                                {phone && (
                                    <li>
                                        <a
                                            href={`tel:${sanitizePhone(phone)}`}
                                            className="text-blue-900 underline hover:no-underline focus-visible:outline-1 focus-visible:outline-slate-300"
                                        >
                                            {phone}
                                        </a>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                    {image && (
                        <div className="col-span-12 md:col-span-5">
                            <ResponsiveImage
                                data={image}
                                className="aspect-4/5 w-full rounded object-cover"
                                loading="eager"
                            />
                        </div>
                    )}
                </div>
            </Container>
        </section>
    )
}
